import React, { useEffect, useRef, useState } from 'react';
import { useT } from '../../i18n/LanguageContext';

/* ------------------------------------------------------------------ */
/*  Localised labels                                                   */
/* ------------------------------------------------------------------ */
const LABELS = {
  de: { main: 'Pause',       sub: 'Wir machen gleich weiter' },
  en: { main: 'Pause',       sub: 'We will resume shortly' },
  fr: { main: 'Pause',       sub: 'Nous reprenons dans un instant' },
  es: { main: 'Pausa',       sub: 'Continuaremos en breve' },
  pt: { main: 'Pausa',       sub: 'Retomaremos em breve' },
  it: { main: 'Pausa',       sub: 'Riprenderemo a breve' },
  nl: { main: 'Pauze',       sub: 'We gaan zo verder' },
  sv: { main: 'Paus',        sub: 'Vi fortsätter strax' },
  pl: { main: 'Przerwa',     sub: 'Wznowimy za chwilę' },
  cs: { main: 'Přestávka',   sub: 'Za chvíli budeme pokračovat' },
  sk: { main: 'Prestávka',   sub: 'Onedlho budeme pokračovať' },
  hu: { main: 'Szünet',      sub: 'Hamarosan folytatjuk' },
  ro: { main: 'Pauză',       sub: 'Continuăm în scurt timp' },
  bg: { main: 'Пауза',       sub: 'Ще продължим след малко' },
  el: { main: 'Διάλειμμα',   sub: 'Θα συνεχίσουμε σύντομα' },
  sr: { main: 'Pauza',       sub: 'Nastavljamo uskoro' },
  sl: { main: 'Odmor',       sub: 'Kmalu nadaljujemo' },
  sq: { main: 'Pushim',      sub: 'Do të vazhdojmë së shpejti' },
  mk: { main: 'Пауза',       sub: 'Продолжуваме наскоро' },
  uk: { main: 'Перерва',     sub: 'Ми продовжимо незабаром' },
  ru: { main: 'Перерыв',     sub: 'Мы скоро продолжим' },
  mt: { main: 'Waqfa',       sub: 'Se nkomplu dalwaqt' },
  lb: { main: 'Paus',        sub: 'Mir maache gläich weider' },
  tr: { main: 'Mola',        sub: 'Kısa süre içinde devam edeceğiz' },
  ar: { main: 'استراحة',      sub: 'سنستأنف قريباً' },
  zh: { main: '休息',         sub: '我们稍后继续' },
  ja: { main: '休憩',         sub: 'まもなく再開します' },
  ko: { main: '휴식',         sub: '곧 다시 시작하겠습니다' },
  hi: { main: 'विराम',        sub: 'हम शीघ्र ही जारी रखेंगे' },
};

const DURATION_MS = 10 * 60 * 1000; // 10 minutes

/**
 * Full-bleed pause slide with a 10-minute countdown timer.
 *
 * The countdown auto-resets to 10:00 every time the slide scrolls into
 * view, so the presenter always gets a fresh break window. It ticks
 * every second while visible, stops at 00:00, and pauses when the
 * slide is scrolled off-screen (no background drift).
 *
 * Left-click advances to the next slide, right-click goes back —
 * standard presentation navigation (handled globally by SmoothScroll).
 */
export default function SectionPause() {
  const { lang } = useT();
  const videoRef = useRef(null);
  const sectionRef = useRef(null);
  // Deadline timestamp (ms since epoch). When null, the timer is idle.
  const [deadline, setDeadline] = useState(null);
  const [remainingMs, setRemainingMs] = useState(DURATION_MS);
  const [tick, setTick] = useState(0); // drives colon blink
  const l = LABELS[lang] || LABELS.en;

  // Countdown startet gekoppelt an das Video (2-Klick-Modell): erst wenn
  // das Video per Klick gestartet wird, läuft auch der Countdown. Das
  // Video enthält bereits die Songspur — es wird KEIN separates Audio mehr
  // abgespielt. Verlassen = Reset.
  useEffect(() => {
    const sec = sectionRef.current;
    if (!sec) return undefined;

    const io = new IntersectionObserver(
      ([entry]) => {
        if (!(entry.isIntersecting && entry.intersectionRatio >= 0.4)) {
          setDeadline(null);
          setRemainingMs(DURATION_MS);
        }
      },
      { threshold: [0, 0.4, 0.6, 1] },
    );
    io.observe(sec);

    const videoEl = videoRef.current;
    const onPlay = () => {
      setDeadline((d) => d ?? Date.now() + DURATION_MS);
    };
    if (videoEl) videoEl.addEventListener('play', onPlay);
    return () => {
      io.disconnect();
      if (videoEl) videoEl.removeEventListener('play', onPlay);
    };
  }, []);

  // Tick every second while the deadline is active.
  useEffect(() => {
    if (deadline == null) return undefined;
    const id = setInterval(() => {
      const left = Math.max(0, deadline - Date.now());
      setRemainingMs(left);
      setTick((t) => t + 1);
      if (left <= 0) clearInterval(id);
    }, 1000);
    // Prime immediately so we don't wait a full second on first render.
    setRemainingMs(Math.max(0, deadline - Date.now()));
    return () => clearInterval(id);
  }, [deadline]);

  const totalSeconds = Math.ceil(remainingMs / 1000);
  const mm = String(Math.floor(totalSeconds / 60)).padStart(2, '0');
  const ss = String(totalSeconds % 60).padStart(2, '0');
  const showColon = tick % 2 === 0;
  const isFinished = remainingMs <= 0;
  const isWarning = !isFinished && remainingMs <= 60 * 1000; // last minute

  // Video pausieren, wenn die Folie NICHT mehr sichtbar ist (Ressourcen
  // schonen). Das Video enthält die Songspur — es startet daher erst per
  // Klick (2-Klick-Modell in SlideDeck), damit die Autoplay-Policy des
  // Browsers erfüllt ist.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return undefined;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          try { v.pause(); v.currentTime = 0; } catch (_) {}
        }
      },
      { threshold: 0.15 },
    );
    io.observe(v);
    return () => io.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="pause"
      data-section
      data-testid="section-pause"
      className="relative w-full overflow-hidden pause-section"
      style={{ minHeight: '100vh' }}
    >
      {/* Diamond Solution soundtrack is now driven by PersistentSlideAudio
          at the PresentationPage level so it survives SlideDeck's lazy
          mount/unmount cycle and can leverage earlier user-gesture credit. */}

      {/* Framed video container — no longer full-bleed */}
      <div className="pause-stage">
        <div className="pause-media">
          <video
            ref={videoRef}
            className="pause-video"
            src="/videos/pause-video.mp4"
            loop
            playsInline
            preload="auto"
            data-testid="pause-video"
            onEnded={(e) => {
              // Belt-and-suspenders: some browsers occasionally ignore the
              // loop attribute after a tab regains focus. Force-restart on
              // the "ended" event so the pause visual never freezes.
              const v = e.currentTarget;
              try { v.currentTime = 0; v.play().catch(() => {}); } catch (_) {}
            }}
          />
          <div className="pause-vignette" aria-hidden="true" />

          <div className="pause-center" data-testid="pause-center">
        <div className="pause-frame">
          <span className="pause-tick" aria-hidden="true" />
          <h2 className="pause-main font-display" data-testid="pause-main">
            {l.main}
          </h2>
          <span className="pause-tick" aria-hidden="true" />
        </div>
        <p className="pause-sub font-ui" data-testid="pause-sub">
          {l.sub}
        </p>

        <div
          className={`pause-clock font-display ${isWarning ? 'is-warning' : ''} ${isFinished ? 'is-finished' : ''}`}
          data-testid="pause-clock"
          aria-live="polite"
        >
          <span>{mm}</span>
          <span
            className={`pause-clock-colon ${showColon ? '' : 'is-off'}`}
            aria-hidden="true"
          >:</span>
          <span>{ss}</span>
        </div>
      </div>
        </div>
      </div>
    </section>
  );
}
